
import { useState, useCallback, memo } from "react";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { DiaryEntry } from "@/utils/diary/types";
import { deleteEntry, updateEntry } from "@/utils/diary/storage";
import { toast } from "sonner";
import EntryCardHeader from "./entry/EntryCardHeader";
import EntryCardContent from "./entry/EntryCardContent";
import EntryCardFooter from "./entry/EntryCardFooter";
import EntryEditDialog from "./entry/editor/EntryEditDialog";
import { useLanguage } from "@/contexts/language";

interface EntryCardProps {
  entry: DiaryEntry;
  onDelete: () => void;
  onUpdate?: (updatedEntry: DiaryEntry) => void;
  readOnly?: boolean;
}

// Memoize the EntryCard component to prevent unnecessary re-renders
const EntryCard = memo(({ entry, onDelete, onUpdate, readOnly = false }: EntryCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { language } = useLanguage();
  
  // Create a safe entry with required default values
  const safeEntry: DiaryEntry = {
    ...entry,
    activities: entry.activities || [],
    milestones: entry.milestones || [],
    age: entry.age || ""
  };
  
  const toggleExpand = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  const handleDelete = useCallback(() => {
    deleteEntry(entry.id)
      .then(() => {
        toast.success(language === 'ja' ? "エントリーが削除されました" : "Entry deleted successfully");
        onDelete();
      })
      .catch((error) => {
        console.error("Error deleting entry:", error);
        toast.error(language === 'ja' ? "エントリーの削除に失敗しました" : "Failed to delete entry");
      });
  }, [entry.id, onDelete, language]);

  const handleSaveEdit = useCallback((updatedEntry: DiaryEntry) => {
    updateEntry(updatedEntry)
      .then(() => {
        if (onUpdate) {
          onUpdate(updatedEntry);
        }
        setDialogOpen(false);
        toast.success(language === 'ja' ? "エントリーが更新されました" : "Entry updated successfully");
      })
      .catch(error => {
        console.error('Error updating diary entry:', error);
        toast.error(language === 'ja' ? "エントリーの更新に失敗しました" : "Failed to update entry");
      });
  }, [onUpdate, language]);

  const handleDateEdit = useCallback((newDate: Date) => {
    const dateString = newDate.toISOString();
    
    const updatedEntry = {
      ...safeEntry,
      createdAt: dateString,
      date: dateString
    };
    
    updateEntry(updatedEntry)
      .then(() => {
        if (onUpdate) {
          onUpdate(updatedEntry);
          toast.success(language === 'ja' ? "日付が更新されました" : "Date updated successfully");
        }
      })
      .catch(error => {
        console.error('Error updating diary entry date:', error);
        toast.error(language === 'ja' ? "日付の更新に失敗しました" : "Failed to update date");
      });
  }, [safeEntry, onUpdate, language]);

  return (
    <Card className="group overflow-hidden transition-all duration-300 hover:shadow-md glass-card border animate-fade-up">
      <CardHeader className="pb-2">
        <EntryCardHeader 
          entry={safeEntry}
          readOnly={readOnly}
          onEditClick={() => setDialogOpen(true)}
          onDateEdit={handleDateEdit}
        />
      </CardHeader>
      
      <CardContent>
        <EntryCardContent 
          entry={safeEntry}
          expanded={expanded}
          toggleExpand={toggleExpand}
          displayText={safeEntry.entryText}
          displayPreviewText={safeEntry.entryText.substring(0, 150) + (safeEntry.entryText.length > 150 ? "..." : "")}
        />
      </CardContent>
      
      <Separator />
      
      <CardFooter>
        <EntryCardFooter 
          entry={safeEntry}
          readOnly={readOnly}
          onDelete={handleDelete}
        />
      </CardFooter>

      <EntryEditDialog 
        isOpen={dialogOpen}
        onOpenChange={setDialogOpen}
        entry={safeEntry}
        onSave={handleSaveEdit}
      />
    </Card>
  );
});

EntryCard.displayName = "EntryCard";

export default EntryCard;
